import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  GetManyV2Dto,
  GetOneDto,
  PageableListV2Dto,
  PageableListV3Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CreateGeofenceModel, EditGeofenceModel, GeofenceDto, GeofenceHistoryEventDto } from '@fleet-beat/api';

@Injectable({
  providedIn: 'root',
})
export class GeofencesApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/geofences`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<GeofenceDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<GeofenceDto[]>) => this.baseApiService.mapResponseV2(response)),
    );
  }

  getOne(id: string, auth: AuthHeaders, params?: GetOneDto): Observable<GeofenceDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<GeofenceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  createOne(data: CreateGeofenceModel, auth: AuthHeaders): Observable<GeofenceDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<GeofenceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<GeofenceDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<GeofenceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOne(id: string, data: EditGeofenceModel, auth: AuthHeaders): Observable<GeofenceDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<GeofenceDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  // NOTE | History

  getGeofenceHistory(
    id: string,
    params: GetManyV2Dto,
    auth: AuthHeaders,
  ): Observable<PageableListV3Dto<GeofenceHistoryEventDto>> {
    return this.baseApiService.get(`${this.baseUrl}/${id}/history`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: PageableListV3Dto<GeofenceHistoryEventDto>) => this.baseApiService.mapResponseV2(response)),
    );
  }
}
